import React, { memo, useCallback } from 'react';
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ProductSearchInput from './ProductSearchInput';
import BarcodesInput from './BarcodesInput';
import { LINE_NUMBER_SX } from '../utils/lineItemCounter';
import { colors, formatCurrency } from '../constants';

// How an existing (matched) product's stored cost (unitPrice) should be treated
// when it appears on this invoice line. Only shown/sent for matched products.
const COST_MODE_OPTIONS = [
  { value: 'keep', label: 'Keep existing cost' },
  { value: 'update', label: 'Update cost to this invoice' },
  { value: 'average_invoice', label: 'Average of invoice prices' },
  { value: 'average_balance', label: 'Average by balance' },
];

const ROW_SX = {
  p: 1.5,
  mb: 2,
  borderRadius: 2,
  border: `1px solid ${colors.border}`,
  backgroundColor: colors.bg,
};
const HEADER_SX = { mb: 1 };
const FIELD_SX = { mt: 1.5 };
const LINE_TOTAL_LABEL_SX = { color: colors.gray600, fontWeight: 500 };
const LINE_TOTAL_VALUE_SX = { fontWeight: 700, color: colors.navy };

const getCategoryLabel = (option) => option?.name ?? '';
const isSameCategory = (option, value) => option.id === value.id;

/**
 * One editable line of an AI-extracted invoice under review.
 *
 * Memoized on `item`, so typing in one line leaves the other lines untouched.
 * That only holds while every callback prop is stable, which is why the row
 * addresses itself by `item.id` instead of by index and why field patches that
 * depend on the line's current value are expressed as updater functions.
 */
function InvoiceImportLineItemRow({
  item,
  categories,
  categoryById,
  showNoMatch,
  hasCategoryError,
  onUpdate,
  onRemove,
  onItemSelected,
  onBarcodeScan,
  onCategoryChange,
}) {
  const { id } = item;

  const handleRemove = useCallback(() => onRemove(id), [onRemove, id]);
  const handleBarcodeScan = useCallback(
    () => onBarcodeScan(id),
    [onBarcodeScan, id],
  );

  const handleSearchChange = useCallback(
    (search) =>
      onUpdate(id, {
        search,
        selectedItem: null,
        matchedProductUuid: null,
        suggestedItem: null,
      }),
    [onUpdate, id],
  );
  const handleItemSelected = useCallback(
    (found) => onItemSelected(id, found),
    [onItemSelected, id],
  );
  const handleUseSuggestion = useCallback(() => {
    if (item.suggestedItem) onItemSelected(id, item.suggestedItem);
  }, [onItemSelected, id, item.suggestedItem]);

  // Unlinking restores the values the AI extracted, so the line never keeps the
  // identity of a product it no longer points at.
  const handleClear = useCallback(
    () =>
      onUpdate(id, (line) => ({
        search: '',
        selectedItem: null,
        matchedProductUuid: null,
        name: line.original?.name ?? '',
        sku: line.original?.sku ?? '',
        barcodes: line.original?.barcodes ?? [],
      })),
    [onUpdate, id],
  );

  const handleNameChange = useCallback(
    (event) => onUpdate(id, { name: event.target.value }),
    [onUpdate, id],
  );
  const handleSkuChange = useCallback(
    (event) => onUpdate(id, { sku: event.target.value }),
    [onUpdate, id],
  );
  const handleBarcodesChange = useCallback(
    (barcodes) => onUpdate(id, { barcodes }),
    [onUpdate, id],
  );
  const handleCategoryChange = useCallback(
    (event, category) => onCategoryChange(id, category),
    [onCategoryChange, id],
  );
  const handleQuantityChange = useCallback(
    (event) => onUpdate(id, { quantity: event.target.value }),
    [onUpdate, id],
  );
  const handleUnitCostChange = useCallback(
    (event) => onUpdate(id, { unitCost: event.target.value }),
    [onUpdate, id],
  );
  const handleSellingPriceChange = useCallback(
    (event) => onUpdate(id, { sellingPrice: event.target.value }),
    [onUpdate, id],
  );
  const handleCostModeChange = useCallback(
    (event) => onUpdate(id, { costMode: event.target.value }),
    [onUpdate, id],
  );

  const lineTotal =
    (parseFloat(item.quantity) || 0) * (parseFloat(item.unitCost) || 0);
  // A linked (existing) product's category is read-only. Resolve the display
  // name from the categories list using the linked product's own categoryId.
  const linkedCategoryId =
    item.selectedItem?.categoryId ?? item.categoryId ?? null;

  return (
    <Box sx={ROW_SX}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={HEADER_SX}
      >
        <Typography variant="caption" sx={LINE_NUMBER_SX} />
        <IconButton size="small" color="error" onClick={handleRemove}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <ProductSearchInput
        value={item.search}
        onChangeText={handleSearchChange}
        onItemSelected={handleItemSelected}
        productType="all"
        selectedItem={item.selectedItem}
        suggestion={item.suggestedItem}
        onUseSuggestion={handleUseSuggestion}
        showNoMatch={showNoMatch}
        onClear={handleClear}
        onBarcodeScan={handleBarcodeScan}
        label="Barcode / Search"
      />

      <Box sx={FIELD_SX}>
        <TextField
          label="Name"
          value={item.name}
          onChange={handleNameChange}
          fullWidth
          size="small"
          placeholder="Product name"
        />
      </Box>

      <Box sx={FIELD_SX}>
        <TextField
          label="SKU"
          value={item.sku}
          onChange={handleSkuChange}
          fullWidth
          size="small"
          placeholder="SKU (optional)"
        />
      </Box>

      <Box sx={FIELD_SX}>
        <BarcodesInput
          label="Barcodes"
          value={item.barcodes}
          onChange={handleBarcodesChange}
        />
      </Box>

      <Box sx={FIELD_SX}>
        {item.matchedProductUuid ? (
          <TextField
            label="Menu Category"
            value={categoryById.get(linkedCategoryId)?.name ?? ''}
            disabled
            fullWidth
            size="small"
            helperText="Category of the existing product (read-only)."
          />
        ) : (
          <Autocomplete
            options={categories}
            getOptionLabel={getCategoryLabel}
            isOptionEqualToValue={isSameCategory}
            value={categoryById.get(item.categoryId) ?? null}
            onChange={handleCategoryChange}
            fullWidth
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Menu Category"
                required={item.productType === 'product'}
                error={hasCategoryError}
                helperText={
                  hasCategoryError
                    ? 'Menu category is required for new products.'
                    : 'New products sync to the menu under this category.'
                }
              />
            )}
          />
        )}
      </Box>

      <Stack direction="row" spacing={2} sx={FIELD_SX}>
        <TextField
          label="Quantity"
          type="number"
          required
          value={item.quantity}
          onChange={handleQuantityChange}
          fullWidth
          size="small"
        />
        <TextField
          label="Unit Cost"
          type="number"
          required
          value={item.unitCost}
          onChange={handleUnitCostChange}
          fullWidth
          size="small"
        />
        <TextField
          label="Price"
          type="number"
          required
          value={item.sellingPrice}
          onChange={handleSellingPriceChange}
          fullWidth
          size="small"
          helperText="Menu price"
        />
      </Stack>

      {item.matchedProductUuid && (
        <Box sx={FIELD_SX}>
          <TextField
            select
            label="Cost Handling"
            value={item.costMode || 'update'}
            onChange={handleCostModeChange}
            fullWidth
            size="small"
            helperText={
              `Current cost: ${
                item.selectedItem?.unitPrice != null
                  ? formatCurrency(Number(item.selectedItem.unitPrice))
                  : 'n/a'
              }` +
              (item.selectedItem?.lastInvoiceUnitPrice != null
                ? ` \u00b7 Last invoice: ${formatCurrency(
                    Number(item.selectedItem.lastInvoiceUnitPrice),
                  )}`
                : '')
            }
          >
            {COST_MODE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={FIELD_SX}
      >
        <Typography variant="caption" sx={LINE_TOTAL_LABEL_SX}>
          Line Total
        </Typography>
        <Typography variant="body2" sx={LINE_TOTAL_VALUE_SX}>
          {formatCurrency(lineTotal)}
        </Typography>
      </Stack>
    </Box>
  );
}

export default memo(InvoiceImportLineItemRow);
